/**
 * EVENT MOCK DATA
 *
 * Centralized mock data for events including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

/**
 * DEFAULT EVENT LIST VIEWS
 */
export const defaultEventListViews: ListView[] = [
  {
    id: 'lv_events_all',
    name: 'All Events',
    apiName: 'all_events',
    objectApiName: 'Event',
    description: 'All calendar events',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Event ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'startDateTime', fieldLabel: 'Start', fieldType: 'datetime', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'organizer', fieldLabel: 'Organizer', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'relatedToName', fieldLabel: 'Related To', fieldType: 'text', isVisible: true, order: 6 },
    ],
    sortBy: {
      fieldApiName: 'startDateTime',
      direction: 'asc'
    },
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_events_today',
    name: 'Today\'s Events',
    apiName: 'todays_events',
    objectApiName: 'Event',
    description: 'Events scheduled for today',
    isDefault: false,
    columns: [
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'startDateTime', fieldLabel: 'Start Time', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'endDateTime', fieldLabel: 'End Time', fieldType: 'datetime', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'location', fieldLabel: 'Location', fieldType: 'text', isVisible: true, order: 5 },
    ],
    sortBy: {
      fieldApiName: 'startDateTime',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_events_week',
    name: 'This Week',
    apiName: 'this_week_events',
    objectApiName: 'Event',
    description: 'Events scheduled this week',
    isDefault: false,
    columns: [
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'startDateTime', fieldLabel: 'Start', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'organizer', fieldLabel: 'Organizer', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 4 },
    ],
    sortBy: {
      fieldApiName: 'startDateTime',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_events_upcoming',
    name: 'Upcoming Meetings',
    apiName: 'upcoming_meetings',
    objectApiName: 'Event',
    description: 'Future meetings and calls',
    isDefault: false,
    columns: [
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'startDateTime', fieldLabel: 'Start', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'relatedToName', fieldLabel: 'Related To', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'organizer', fieldLabel: 'Organizer', fieldType: 'text', isVisible: true, order: 4 },
    ],
    sortBy: {
      fieldApiName: 'startDateTime',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

/**
 * DEFAULT EVENT RECORDS
 */
export const defaultEventRecords: ListViewRecord[] = [
  {
    id: 'event_001',
    subject: 'Quarterly Business Review with Tech Innovations',
    description: 'Review Q1 performance and discuss expansion opportunities',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'High',
    startDateTime: '2025-11-05T10:00:00Z',
    endDateTime: '2025-11-05T11:30:00Z',
    duration: 90,
    location: 'Conference Room A',
    isAllDay: false,
    isRecurring: false,
    organizer: 'John Smith',
    owner: 'John Smith',
    relatedToName: 'Tech Innovations Inc',
    relatedToType: 'Account',
    reminder: 15,
    createdDate: '2025-10-28T09:00:00Z',
    lastModifiedDate: '2025-11-03T14:30:00Z'
  },
  {
    id: 'event_002',
    subject: 'Product Demo for Global Manufacturing',
    description: 'Showcase new cloud migration features',
    type: 'Demo',
    status: 'Scheduled',
    priority: 'High',
    startDateTime: '2025-11-05T14:00:00Z',
    endDateTime: '2025-11-05T15:00:00Z',
    duration: 60,
    location: 'Virtual - Zoom',
    isAllDay: false,
    isRecurring: false,
    organizer: 'Sarah Johnson',
    owner: 'Sarah Johnson',
    relatedToName: 'Global Manufacturing Corp',
    relatedToType: 'Opportunity',
    reminder: 30,
    createdDate: '2025-10-30T11:00:00Z',
    lastModifiedDate: '2025-11-04T10:15:00Z'
  },
  {
    id: 'event_003',
    subject: 'Follow-up Call - Retail Solutions',
    description: 'Discuss custom reporting requirements',
    type: 'Call',
    status: 'Tentative',
    priority: 'Normal',
    startDateTime: '2025-11-05T15:30:00Z',
    endDateTime: '2025-11-05T16:00:00Z',
    duration: 30,
    isAllDay: false,
    isRecurring: false,
    organizer: 'Michael Chen',
    relatedToName: 'Emily Rodriguez',
    relatedToType: 'Contact',
    reminder: 10,
    createdDate: '2025-11-15T13:00:00Z',
    lastModifiedDate: '2025-11-16T09:00:00Z'
  },
  {
    id: 'event_004',
    subject: 'Team Standup Meeting',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'Normal',
    startDateTime: '2025-11-18T09:00:00Z',
    endDateTime: '2025-11-18T09:30:00Z',
    duration: 30,
    location: 'Conference Room B',
    isAllDay: false,
    isRecurring: true,
    organizer: 'David Wilson',
    reminder: 5,
    createdDate: '2025-11-01T08:00:00Z',
    lastModifiedDate: '2025-11-01T08:00:00Z'
  },
  {
    id: 'event_005',
    subject: 'Proposal Submission Deadline',
    description: 'Submit proposal for Financial Services Group',
    type: 'Deadline',
    status: 'Scheduled',
    priority: 'High',
    startDateTime: '2025-11-19T17:00:00Z',
    endDateTime: '2025-11-19T17:00:00Z',
    duration: 0,
    isAllDay: false,
    isRecurring: false,
    organizer: 'Emily Davis',
    relatedToName: 'Financial Services Group',
    relatedToType: 'Opportunity',
    reminder: 60,
    createdDate: '2025-11-10T14:00:00Z',
    lastModifiedDate: '2025-11-16T11:30:00Z'
  },
  {
    id: 'event_006',
    subject: 'Customer Success Training',
    description: 'New features training for support team',
    type: 'Training',
    status: 'Confirmed',
    priority: 'Normal',
    startDateTime: '2025-11-20T13:00:00Z',
    endDateTime: '2025-11-20T16:00:00Z',
    duration: 180,
    location: 'Training Room',
    isAllDay: false,
    isRecurring: false,
    organizer: 'Sarah Johnson',
    reminder: 30,
    createdDate: '2025-11-08T10:00:00Z',
    lastModifiedDate: '2025-11-14T15:00:00Z'
  },
  {
    id: 'event_007',
    subject: 'Weekly Sales Pipeline Review',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'Normal',
    startDateTime: '2025-11-20T10:00:00Z',
    endDateTime: '2025-11-20T11:00:00Z',
    duration: 60,
    location: 'Conference Room A',
    isAllDay: false,
    isRecurring: true,
    organizer: 'John Smith',
    reminder: 15,
    createdDate: '2024-12-01T09:00:00Z',
    lastModifiedDate: '2024-12-01T09:00:00Z'
  },
  {
    id: 'event_008',
    subject: 'Discovery Call - Education Tech Solutions',
    description: 'Initial needs assessment',
    type: 'Call',
    status: 'Scheduled',
    priority: 'Normal',
    startDateTime: '2025-11-21T11:00:00Z',
    endDateTime: '2025-11-21T11:45:00Z',
    duration: 45,
    isAllDay: false,
    isRecurring: false,
    organizer: 'John Smith',
    relatedToName: 'Lisa Wong',
    relatedToType: 'Lead',
    reminder: 15,
    createdDate: '2025-11-16T14:00:00Z',
    lastModifiedDate: '2025-11-16T14:00:00Z'
  },
  {
    id: 'event_009',
    subject: 'Industry Conference - Cloud Summit 2025',
    type: 'Conference',
    status: 'Confirmed',
    priority: 'Normal',
    startDateTime: '2025-11-25T00:00:00Z',
    endDateTime: '2025-11-27T00:00:00Z',
    isAllDay: true,
    isRecurring: false,
    location: 'San Francisco Convention Center',
    organizer: 'Sarah Johnson',
    reminder: 1440,
    createdDate: '2024-11-15T10:00:00Z',
    lastModifiedDate: '2025-11-10T16:00:00Z'
  },
  {
    id: 'event_010',
    subject: 'Escalated Case Review - Software Dynamics',
    description: 'Discuss user permissions bug',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'High',
    startDateTime: '2025-11-22T09:00:00Z',
    endDateTime: '2025-11-22T10:00:00Z',
    duration: 60,
    location: 'Virtual - Teams',
    isAllDay: false,
    isRecurring: false,
    organizer: 'Michael Chen',
    relatedToName: 'C-00010',
    relatedToType: 'Case',
    reminder: 30,
    createdDate: '2025-11-16T15:30:00Z',
    lastModifiedDate: '2025-11-16T15:30:00Z'
  },
  {
    id: 'event_011',
    subject: 'Contract Renewal Discussion',
    description: 'Review renewal terms with Construction Pro',
    type: 'Meeting',
    status: 'Scheduled',
    priority: 'Normal',
    startDateTime: '2025-11-23T14:00:00Z',
    endDateTime: '2025-11-23T15:00:00Z',
    duration: 60,
    location: 'Conference Room C',
    isAllDay: false,
    isRecurring: false,
    organizer: 'Emily Davis',
    relatedToName: 'Construction Pro LLC',
    relatedToType: 'Account',
    reminder: 30,
    createdDate: '2025-11-14T11:00:00Z',
    lastModifiedDate: '2025-11-16T13:00:00Z'
  },
  {
    id: 'event_012',
    subject: 'Send Proposal Email - Marketing Pros',
    type: 'Email',
    status: 'Scheduled',
    priority: 'Normal',
    startDateTime: '2025-11-24T10:00:00Z',
    endDateTime: '2025-11-24T10:00:00Z',
    duration: 0,
    isAllDay: false,
    isRecurring: false,
    organizer: 'Sarah Johnson',
    relatedToName: 'Marketing Pros',
    relatedToType: 'Opportunity',
    reminder: 15,
    createdDate: '2025-11-17T09:00:00Z',
    lastModifiedDate: '2025-11-17T09:00:00Z'
  },
  {
    id: 'event_013',
    subject: 'Client Onboarding - Healthcare Innovations',
    description: 'Kickoff meeting for new implementation',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'High',
    startDateTime: '2025-11-24T13:00:00Z',
    endDateTime: '2025-11-24T14:30:00Z',
    duration: 90,
    location: 'Virtual - Zoom',
    isAllDay: false,
    isRecurring: false,
    organizer: 'David Wilson',
    relatedToName: 'Healthcare Innovations',
    relatedToType: 'Opportunity',
    reminder: 60,
    createdDate: '2025-11-15T10:15:00Z',
    lastModifiedDate: '2025-11-16T12:00:00Z'
  },
  {
    id: 'event_014',
    subject: 'Check-in Call - Logistics Central',
    description: 'Monthly account review',
    type: 'Call',
    status: 'Tentative',
    priority: 'Low',
    startDateTime: '2025-11-28T15:00:00Z',
    endDateTime: '2025-11-28T15:30:00Z',
    duration: 30,
    isAllDay: false,
    isRecurring: true,
    organizer: 'John Smith',
    relatedToName: 'Logistics Central',
    relatedToType: 'Account',
    reminder: 15,
    createdDate: '2024-12-28T14:00:00Z',
    lastModifiedDate: '2025-11-15T14:00:00Z'
  },
  {
    id: 'event_015',
    subject: 'Product Roadmap Planning',
    description: 'Q1 roadmap review and prioritization',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'High',
    startDateTime: '2025-11-29T10:00:00Z',
    endDateTime: '2025-11-29T12:00:00Z',
    duration: 120,
    location: 'Conference Room A',
    isAllDay: false,
    isRecurring: false,
    organizer: 'Emily Davis',
    reminder: 30,
    createdDate: '2025-11-12T11:30:00Z',
    lastModifiedDate: '2025-11-16T16:00:00Z'
  },
  {
    id: 'event_016',
    subject: 'Board Meeting Preparation',
    type: 'Task',
    status: 'Scheduled',
    priority: 'High',
    startDateTime: '2025-11-30T09:00:00Z',
    endDateTime: '2025-11-30T12:00:00Z',
    duration: 180,
    isAllDay: false,
    isRecurring: false,
    organizer: 'Sarah Johnson',
    reminder: 60,
    createdDate: '2025-11-15T08:00:00Z',
    lastModifiedDate: '2025-11-16T10:00:00Z'
  },
  {
    id: 'event_017',
    subject: 'Cancelled: Budget Review Meeting',
    description: 'Rescheduling due to conflicts',
    type: 'Meeting',
    status: 'Cancelled',
    priority: 'Normal',
    startDateTime: '2025-11-18T14:00:00Z',
    endDateTime: '2025-11-18T15:00:00Z',
    duration: 60,
    location: 'Conference Room B',
    isAllDay: false,
    isRecurring: false,
    organizer: 'Michael Chen',
    createdDate: '2025-11-10T09:00:00Z',
    lastModifiedDate: '2025-11-16T16:45:00Z'
  },
  {
    id: 'event_018',
    subject: 'Annual Planning Session',
    type: 'Meeting',
    status: 'Confirmed',
    priority: 'High',
    startDateTime: '2025-02-01T00:00:00Z',
    endDateTime: '2025-02-01T00:00:00Z',
    isAllDay: true,
    isRecurring: false,
    location: 'Offsite - Retreat Center',
    organizer: 'David Wilson',
    reminder: 2880,
    createdDate: '2024-12-15T10:00:00Z',
    lastModifiedDate: '2025-11-10T14:00:00Z'
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedEventListViews = createPersistedMockData(
  'crm_event_list_views',
  defaultEventListViews
);

export const persistedEventRecords = createPersistedMockData(
  'crm_event_records',
  defaultEventRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockEventListViews = defaultEventListViews;
export const mockEventRecords = defaultEventRecords;
